/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import { useCallback, useContext, useMemo, useState } from "react";
import { useTable } from "react-table";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import { TableContainerWrapper } from "../../../../components/shared/Table/style";
import { ITableColumn } from "../../../../interfaces/Table/TableColumn";
import {
  GetRfqs200ResponseBlocksInner,
  GetRfqs200ResponseBlocksInnerLegsInner,
  SideResponse,
} from "../../../../codegen-api";
import { DefaultCellForColumn } from "../../../../components/shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../../../components/shared/Table/DefaultHeaderForColumn";
import { BlockRow, RFQTableWrapper } from "./style";
import RequestAcceptRFQModal from "../modals/RequestAcceptRFQModal";
import { useRFQ } from "../../../../hooks/api/rfq/useRFQ";
import { useSFX } from "../../../../hooks/useSFX";
import { useToast } from "../../../../hooks/toast";
import { ToastEnum, ToastStatusEnum } from "../../../../utils/toast";
import { EmptyContent } from "../../../../components/TradeHistory/style";
import { SPACING } from "../../../../constants/design/spacing";
import { useQuotes } from "../../../../hooks/api/rfq/useQuotes";
import { MarketInstrumentContext } from "../../../../contexts/MarketInstrumentContext";
import { generateLegTag } from "./cells";
import { buildRFQColumns } from "./columns";

function RFQTable() {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [selectedBlock, setBlock] = useState<GetRfqs200ResponseBlocksInner>();
  const [selectedSide, setSide] = useState<SideResponse>();
  const [showRFQModal, setShowRFQModal] = useState<boolean>(false);
  const { data: rfqData, deleteRFQ, mutate: mutateRFQs } = useRFQ();
  const { data: quotesData, deleteQuote, acceptQuote } = useQuotes();
  const { t: translateAll } = useTranslation();
  const { t } = useTranslation("app", { keyPrefix: "pages.RFQPage.RFQTable" });
  const { t: apiErrors } = useTranslation("apiErrors");
  const { playSound } = useSFX();
  const { addToast, addErrorToast } = useToast();
  const { getMarketPrecision } = useContext(MarketInstrumentContext);

  const handleRowClick = (index: number) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter((i) => i !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const quoteByBlock = useCallback(
    (blockId: string, side: SideResponse) => {
      if (!quotesData?.quotes) return false;

      const quote = (quotesData.quotes || []).find(
        (q) =>
          q.block_id === blockId &&
          (q.is_buy ? SideResponse.Buy : SideResponse.Sell) === side
      );
      return quote;
    },
    [quotesData]
  );

  const memoizedBlocks = useMemo(() => {
    // sort by role === taker first
    const sortedBlocks = (rfqData?.blocks || [])
      .sort((a, b) => Number(b.created_timestamp) - Number(a.created_timestamp))
      .sort((a) => {
        if (a.role === "taker") return -1;
        return 0;
      });
    return sortedBlocks;
  }, [rfqData]);

  const onOffer = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      block: GetRfqs200ResponseBlocksInner,
      side: SideResponse
    ) => {
      e.stopPropagation();
      setBlock(block);
      setSide(side);
      setShowRFQModal(true);
    },
    []
  );

  const onCancelBlock = useCallback(
    async (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      block: GetRfqs200ResponseBlocksInner
    ) => {
      e.stopPropagation();
      try {
        await deleteRFQ(block.block_id);
        playSound("order_cancelled", true);
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "RFQ Cancelled",
            subheader: "RFQ cancelled successfully",
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
      } catch (error: any) {
        addErrorToast(
          t("cancel_rfq_order"),
          apiErrors(error.message) || t("try_again")
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addErrorToast, addToast, apiErrors, playSound, t]
  );

  const onCancelQuote = useCallback(
    async (quoteId: string) => {
      const quote = (quotesData?.quotes || []).find(
        (q) => q.quote_id === quoteId
      );
      try {
        await deleteQuote(quoteId);
        playSound("order_cancelled", true);
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: `${quote?.is_buy ? "Bid" : "Ask"} Cancelled`,
            subheader: "Quote cancelled successfully",
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
      } catch (error: any) {
        addErrorToast(
          t("cancel_quote_order"),
          apiErrors(error.message) || t("try_again")
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addErrorToast, addToast, apiErrors, playSound, t]
  );

  const onHideModal = useCallback(() => {
    setShowRFQModal(false);
  }, []);

  const getHighestPrecision = useCallback(
    (legs: GetRfqs200ResponseBlocksInnerLegsInner[]) => {
      let highestPrecision = 2;
      legs.forEach((leg) => {
        const precision = getMarketPrecision(leg.asset, leg.instrument_type);
        if (precision.price_precision > highestPrecision)
          highestPrecision = precision.price_precision;
      });

      return highestPrecision;
    },
    [getMarketPrecision]
  );

  const columns: ITableColumn<GetRfqs200ResponseBlocksInner>[] = useMemo(
    () =>
      buildRFQColumns({
        t,
        expandedRows,
        generateLegTag,
        quoteByBlock,
        onOffer,
        onCancelQuote,
        getHighestPrecision,
        onCancelBlock,
        translateAll,
      }),
    [
      t,
      expandedRows,
      generateLegTag,
      quoteByBlock,
      onOffer,
      onCancelQuote,
      getHighestPrecision,
      onCancelBlock,
      translateAll,
    ]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: memoizedBlocks,
      defaultColumn: {
        Header: DefaultHeaderForColumn,
        Cell: DefaultCellForColumn,
      } as any,
    });

  return (
    <div>
      <RequestAcceptRFQModal
        block={selectedBlock}
        side={selectedSide}
        show={showRFQModal}
        onHide={onHideModal}
        acceptQuote={acceptQuote}
        mutateRFQs={mutateRFQs}
      />
      <TableContainerWrapper>
        <AnimatePresence>
          <RFQTableWrapper>
            {memoizedBlocks.length > 0 ? (
              <table {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => {
                    const { key, ...trProps } =
                      headerGroup.getHeaderGroupProps();
                    return (
                      <tr key={key} {...trProps}>
                        {headerGroup.headers.map((column: any) => {
                          const { key, ...thProps } = column.getHeaderProps();
                          return (
                            <th key={key} {...thProps}>
                              {column.render("Header")}
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    const { key, ...rowProps } = row.getRowProps();
                    const isTaker = row.original.role === "taker";
                    const isExpanded = expandedRows.includes(row.index);

                    return (
                      <BlockRow
                        key={key}
                        {...rowProps}
                        $isTaker={isTaker}
                        onClick={() =>
                          (row.original.legs || []).length > 1
                            ? handleRowClick(row.index)
                            : null
                        }
                      >
                        {row.cells.map((cell) => {
                          const { key, ...tdProps } = cell.getCellProps();
                          return (
                            <td
                              key={key}
                              style={{
                                verticalAlign: isExpanded ? "top" : "middle",
                              }}
                              {...tdProps}
                            >
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </BlockRow>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <EmptyContent style={{ padding: SPACING.five }}>
                There are no RFQ listings
              </EmptyContent>
            )}
          </RFQTableWrapper>
        </AnimatePresence>
      </TableContainerWrapper>
    </div>
  );
}
export default RFQTable;
